// Storage key constants for login analytics session guards
export const LOGIN_RECORDED_SESSION_KEY = "kaboodle.loginAnalyticsRecorded";
export const SESSION_GUARD_TOKEN_KEY = "kaboodle.sessionGuardToken";
export const LOGIN_RECORDING_LOCK_KEY = "kaboodle.loginAnalyticsLock";
export const LOGIN_RECORDED_RECENTLY_KEY =
  "kaboodle.loginAnalyticsRecordedRecently";
export const LOGIN_RECORDING_PENDING = "pending";
export const LOGIN_RECORDING_DONE = "true";
export const LOGIN_RECORDING_LOCK_TTL_MS = 15_000;
export const RECENT_LOGIN_RECORD_WINDOW_MS = 2 * 60 * 1000;

// The `trigger` value used for the first token fetch on mount. Login analytics
// are only recorded for this trigger, so it must stay in sync between the guard
// (`shouldRecordLoginAnalytics`) and the payload built in `useAuthSession`.
export const LOGIN_TRIGGER_INITIAL_MOUNT = "initial mount";

export type LoginRecordingLock = {
  owner: string;
  expiresAt: number;
};

/**
 * Exponential backoff delay capped at 30 seconds.
 * attempt=0 → 1s, attempt=1 → 2s, attempt=4 → 16s, attempt=5+ → 30s
 */
export const getBackoffDelay = (attempt: number): number =>
  Math.min(1000 * Math.pow(2, attempt), 30_000);

export const getRecentLoginRecordedAt = (): number | null => {
  const value = localStorage.getItem(LOGIN_RECORDED_RECENTLY_KEY);
  if (!value) return null;

  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

export const hasRecentlyRecordedLoginInBrowser = (): boolean => {
  const recordedAt = getRecentLoginRecordedAt();
  if (!recordedAt) return false;
  return Date.now() - recordedAt < RECENT_LOGIN_RECORD_WINDOW_MS;
};

export const setRecentlyRecordedLoginInBrowser = (): void => {
  localStorage.setItem(LOGIN_RECORDED_RECENTLY_KEY, String(Date.now()));
};

export const getLoginRecordingLock = (): LoginRecordingLock | null => {
  const value = localStorage.getItem(LOGIN_RECORDING_LOCK_KEY);
  if (!value) return null;

  try {
    const parsed = JSON.parse(value) as LoginRecordingLock;
    if (
      typeof parsed.owner !== "string" ||
      typeof parsed.expiresAt !== "number"
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const hasActiveLoginRecordingLock = (): boolean => {
  const lock = getLoginRecordingLock();
  return !!lock && lock.expiresAt > Date.now();
};

/**
 * Best-effort guard against two tabs recording the same login concurrently.
 *
 * This is NOT a true mutex. `localStorage.setItem` followed by `getItem` is not
 * an atomic compare-and-swap across tabs, so the write-then-read-back below only
 * catches a competing tab if its write lands in the sub-millisecond window
 * between this tab's `setItem` and `getItem` — in practice it almost always
 * reads back its own write. Combined with the short lock TTL and the
 * `LOGIN_RECORDED_RECENTLY_KEY` recency check, this is good enough to suppress
 * duplicate analytics in the common case; it does not provide hard mutual
 * exclusion.
 */
export const acquireLoginRecordingLock = (): string | null => {
  const now = Date.now();
  const existingLock = getLoginRecordingLock();

  if (existingLock && existingLock.expiresAt > now) {
    return null;
  }

  const candidateLock: LoginRecordingLock = {
    owner: `${now}-${Math.random().toString(36).slice(2)}`,
    expiresAt: now + LOGIN_RECORDING_LOCK_TTL_MS,
  };

  localStorage.setItem(LOGIN_RECORDING_LOCK_KEY, JSON.stringify(candidateLock));

  const confirmedLock = getLoginRecordingLock();
  if (!confirmedLock || confirmedLock.owner !== candidateLock.owner) {
    return null;
  }

  return candidateLock.owner;
};

export const releaseLoginRecordingLock = (owner: string): void => {
  const existingLock = getLoginRecordingLock();
  if (!existingLock || existingLock.owner !== owner) {
    return;
  }

  localStorage.removeItem(LOGIN_RECORDING_LOCK_KEY);
};

export const getHasRecordedLoginInSession = (): boolean => {
  const value = sessionStorage.getItem(LOGIN_RECORDED_SESSION_KEY);
  if (value === LOGIN_RECORDING_DONE || value === LOGIN_RECORDING_PENDING) {
    return true;
  }

  return hasRecentlyRecordedLoginInBrowser();
};

export const setHasRecordedLoginInSession = (): void => {
  sessionStorage.setItem(LOGIN_RECORDED_SESSION_KEY, LOGIN_RECORDING_DONE);
  setRecentlyRecordedLoginInBrowser();
};

export const setLoginRecordingPendingInSession = (): void => {
  sessionStorage.setItem(LOGIN_RECORDED_SESSION_KEY, LOGIN_RECORDING_PENDING);
};

// Clears the "login already recorded" markers for this session/browser so a
// fresh login can be recorded again (used on the failure-reset and logout
// paths). It deliberately does NOT touch LOGIN_RECORDING_LOCK_KEY: the lock is
// owner-scoped and must only be dropped by its holder via
// `releaseLoginRecordingLock(owner)` (or left to expire via its TTL).
// Removing it here would evict a lock held by another tab that is mid-recording.
export const clearRecordedLoginInSession = (): void => {
  sessionStorage.removeItem(LOGIN_RECORDED_SESSION_KEY);
  localStorage.removeItem(LOGIN_RECORDED_RECENTLY_KEY);
};

/**
 * Single source of truth for "a login has already been recorded (or is being
 * recorded) and must not be recorded again". It combines the in-memory ref
 * (this hook instance) with the session/browser markers (this tab and other
 * tabs via the recency flag).
 *
 * Both the trigger-level guard (`shouldRecordLoginAnalytics`) and the recording
 * entry point in `useAuthSession` build on this so the two checks can't drift
 * apart. The lock check (`hasActiveLoginRecordingLock`) is intentionally NOT
 * part of this predicate: it guards concurrent in-flight attempts and is only
 * relevant at the moment a tab is about to record, not when deciding whether a
 * trigger is eligible at all.
 */
export const isLoginRecordedOrInProgress = ({
  hasRecordedLoginRef,
}: {
  hasRecordedLoginRef: boolean;
}): boolean => hasRecordedLoginRef || getHasRecordedLoginInSession();

export const shouldRecordLoginAnalytics = ({
  trigger,
  hasRecordedLoginRef,
}: {
  trigger: string;
  hasRecordedLoginRef: boolean;
}): boolean =>
  trigger === LOGIN_TRIGGER_INITIAL_MOUNT &&
  !isLoginRecordedOrInProgress({ hasRecordedLoginRef });

// A tab that was hidden longer than this may have missed a periodic refresh
// while backgrounded, so its token could already be stale.
export const VISIBILITY_STALE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes
// Refresh eagerly on visibility if less than this much of the token's life
// remains, rather than waiting for the next periodic refresh to possibly
// land after the token has already expired.
export const VISIBILITY_REFRESH_EXPIRY_BUFFER_SEC = 120; // 2 minutes

/**
 * Decide whether a tab becoming visible again should eagerly refresh its
 * access token, rather than waiting for the next periodic refresh.
 */
export const shouldRefreshTokenOnVisibilityChange = ({
  wasHiddenForMs,
  tokenExpiresInSec,
}: {
  wasHiddenForMs: number;
  tokenExpiresInSec: number;
}): boolean =>
  wasHiddenForMs > VISIBILITY_STALE_THRESHOLD_MS ||
  tokenExpiresInSec < VISIBILITY_REFRESH_EXPIRY_BUFFER_SEC;

export const isAuthRefreshError = (errorMessage: string): boolean => {
  const message = errorMessage.toLowerCase();
  return (
    message.includes("unauthorized") ||
    message.includes("invalid_token") ||
    message.includes("access_denied")
  );
};

export const isNetworkRefreshError = ({
  errorMessage,
  isOnline,
}: {
  errorMessage: string;
  isOnline: boolean;
}): boolean => {
  const message = errorMessage.toLowerCase();
  return !isOnline || message.includes("network") || message.includes("fetch");
};

/**
 * When a visibility-triggered token refresh fails, decide whether it's safe
 * to log the user out. We only log out on a refresh error that looks like a
 * real auth failure and does NOT also look like a network issue — a
 * mis-classification here either logs a user out during a transient network
 * blip, or leaves a genuinely unauthenticated user silently signed in.
 */
export const shouldLogoutAfterVisibilityRefreshFailure = ({
  errorMessage,
  isOnline,
}: {
  errorMessage: string;
  isOnline: boolean;
}): boolean =>
  isAuthRefreshError(errorMessage) &&
  !isNetworkRefreshError({ errorMessage, isOnline });
